import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import TextInput from '../../components/TextInput';
import TextAreaInput from '../../components/TextAreaInput';
import Button from '../../components/Button';
import SkeletonLoader from '../../components/SkeletonLoader';
import roleApi from '../../api/roleApi';
import permissionApi from '../../api/permissionApi';
import { useToast } from '../../contexts/ToastContext';
import CheckboxInput from '../../components/CheckboxInput';

const EditRole = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { addToast } = useToast();
  const [loading, setLoading] = useState(true);
  const [generalError, setGeneralError] = useState(null);
  const [validationErrors, setValidationErrors] = useState({});
  const [allPermissions, setAllPermissions] = useState([]);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    permissions: [],
  });
  const [roleFound, setRoleFound] = useState(true);

  useEffect(() => {
    const fetchRoleAndPermissions = async () => {
      setLoading(true);
      setGeneralError(null);
      setValidationErrors({});
      try {
        const roleResponse = await roleApi.getRoleByIdentifier(id);
        if (roleResponse.success && roleResponse.data) {
          const roleToEdit = roleResponse.data;
          setFormData(prev => ({
            ...prev,
            name: roleToEdit.name,
            description: roleToEdit.description,
            permissions: roleToEdit.permissions.map(permission => permission.id.toString()),
          }));
          setRoleFound(true);
        } else {
          setRoleFound(false);
          setGeneralError(roleResponse.message || 'Role not found.');
        }

        const permissionsResponse = await permissionApi.getPermissions();
        setAllPermissions(permissionsResponse.data.map(p => ({ ...p, id: p.id.toString() })));

      } catch (err) {
        console.error('Failed to fetch Role or Permissions:', err);
        setRoleFound(false);
        setGeneralError(err.response?.data?.message || 'Failed to load Role or Permissions. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchRoleAndPermissions();
  }, [id, addToast]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value,
    });
    if (validationErrors[name]) {
      setValidationErrors(prev => ({ ...prev, [name]: undefined }));
    }
  };

  const handlePermissionChange = (e, permissionId) => {
    const { checked } = e.target;
    setFormData(prev => {
      const newPermissions = checked
        ? [...prev.permissions, permissionId]
        : prev.permissions.filter(id => id !== permissionId);
      return { ...prev, permissions: newPermissions };
    });
    if (validationErrors.permissions) {
      setValidationErrors(prev => ({ ...prev, permissions: undefined }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setGeneralError(null);
    setValidationErrors({});

    const data = new FormData();
    for (const key in formData) {
      if (key === 'permissions') {
        formData.permissions.forEach(permissionId => data.append('permissions[]', parseInt(permissionId, 10)));
      } else {
        data.append(key, formData[key]);
      }
    }

    try {
      await roleApi.updateRole(id, data);
      addToast('Role updated successfully!', 'success');
      navigate('/roles');
    } catch (err) {
      console.error('Failed to update Role:', err);
      if (err.response && err.response.status === 422) {
        setValidationErrors(err.response.data.errors || {});
        addToast(err.response.data.message || 'Validation failed. Please check your input.', 'error');
      } else {
        addToast(err.response?.data?.message || 'Failed to update Role. Please try again.', 'error');
      }
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <SkeletonLoader type="form" />
      </div>
    );
  }

  if (!roleFound) {
    return (
      <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white text-center">
        <h1 className="text-3xl font-bold mb-4">Role Not Found</h1>
        <p className="text-text-gray">{generalError || 'The Role you are trying to edit does not exist.'}</p>
        <Button
          onClick={() => navigate('/roles')}
          className="mt-4 bg-gray-500 hover:bg-gray-600 text-white"
        >
          Go to Roles List
        </Button>
      </div>
    );
  }

  return (
    <div className="p-6 bg-gray-800 rounded-lg shadow-md text-text-white">
      <h1 className="text-3xl font-bold mb-6">Edit Role</h1>
      {generalError && <div className="text-red-500 mb-4">{generalError}</div>}
      <form onSubmit={handleSubmit}>
        <TextInput
          label="Role Name"
          name="name"
          id="name"
          value={formData.name}
          onChange={handleChange}
          required={true}
          placeholder="Role Name"
          error={validationErrors?.name?.[0]}
        />
        <TextAreaInput
          label="Description"
          name="description"
          id="description"
          value={formData.description}
          onChange={handleChange}
          required={true}
          rows={4}
          placeholder="Role Description"
          error={validationErrors?.description?.[0]}
        />

        <div className="mb-4">
          <label className="block text-text-gray text-sm font-bold mb-2">Permissions:</label>
          {validationErrors?.permissions && (
            <p className="text-red-500 text-xs italic mb-2">{validationErrors.permissions[0]}</p>
          )}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {allPermissions.map(permission => {
              const isChecked = formData.permissions.includes(permission.id);
              return (
                <CheckboxInput
                  key={permission.id}
                  label={permission.name.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase())}
                  name="permissions"
                  id={`permission-${permission.id}`}
                  value={permission.id}
                  checked={isChecked ? 1 : 0}
                  onChange={(e) => handlePermissionChange(e, permission.id)}
                />
              );
            })}
          </div>
        </div>

        <div className="flex items-center justify-between mt-6">
          <Button
            type="submit"
            className="bg-accent-red hover:bg-red-700 text-white"
            loading={loading}
          >
            Update Role
          </Button>
          <Button
            type="button"
            onClick={() => navigate('/roles')}
            className="bg-gray-500 hover:bg-gray-600 text-white"
            disabled={loading}
          >
            Cancel
          </Button>
        </div>
      </form>
    </div>
  );
};

export default EditRole;